import { extractRecipeSizeLabel, normalizeSizeLabel } from "./recipeCatalogTags.js";

export const HAUL_RATE_BASES = ["none", "flat_usd", "building_sq_ft", "building_length"] as const;
export type HaulRateBasis = (typeof HAUL_RATE_BASES)[number];

export const HAUL_RATE_SCOPES = ["group", "recipe"] as const;
export type HaulRateScope = (typeof HAUL_RATE_SCOPES)[number];

export type HaulRateRuleInput = {
  id: string;
  scope: HaulRateScope;
  group_id: string | null;
  recipe_id: string | null;
  basis: HaulRateBasis;
  plant_rate_usd: number;
  lot_rate_usd: number;
};

export type HaulProductInput = {
  recipe_id: string;
  kind: string;
  recipe_code: string;
  name: string;
  recipe_group_id: string | null;
  /** Stored size, such as `10x16`. Preferred over reading the code or name. */
  size_label?: string | null;
  tags?: readonly string[];
};

export type HaulDollarWrite = {
  recipe_id: string;
  plant_usd: number;
  lot_usd: number;
};

export type HaulEvalPlan = {
  writes: HaulDollarWrite[];
  clear_recipe_ids: string[];
  missing_size: Array<{ recipe_id: string; recipe_code: string; name: string }>;
};

export function parseHaulRateBasis(raw: unknown): HaulRateBasis | null {
  if (typeof raw !== "string") return null;
  const key = raw.trim();
  return (HAUL_RATE_BASES as readonly string[]).includes(key) ? (key as HaulRateBasis) : null;
}

export function parseHaulRateScope(raw: unknown): HaulRateScope | null {
  if (typeof raw !== "string") return null;
  const key = raw.trim();
  return (HAUL_RATE_SCOPES as readonly string[]).includes(key) ? (key as HaulRateScope) : null;
}

function dimensionsFromLabel(label: string | null | undefined): { width: number; length: number } | null {
  if (!label?.trim()) return null;
  const normalized = normalizeSizeLabel(label);
  if (!normalized) return null;
  const [width, length] = normalized.split("x").map(Number);
  if (!width || !length) return null;
  return { width, length };
}

/**
 * Stored `size_label` first. Otherwise the shared catalog recognizer
 * (`size:` tags, then a size token in the name or code).
 */
export function haulSizeForProduct(product: {
  size_label?: string | null;
  recipe_code: string;
  name: string;
  tags?: readonly string[];
}): { width: number; length: number } | null {
  return (
    dimensionsFromLabel(product.size_label) ??
    dimensionsFromLabel(
      extractRecipeSizeLabel({
        code: product.recipe_code,
        name: product.name,
        tags: product.tags,
      }),
    )
  );
}

export function parseColumnCalc(raw: unknown): { basis: HaulRateBasis; rate_usd: number } | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const rec = raw as Record<string, unknown>;
  const basis = parseHaulRateBasis(rec.basis);
  if (!basis) return null;
  if (basis === "none") return { basis, rate_usd: 0 };
  const rate = typeof rec.rate_usd === "number" ? rec.rate_usd : Number(rec.rate_usd);
  if (!Number.isFinite(rate) || rate < 0) return null;
  return { basis, rate_usd: Math.round(rate * 10000) / 10000 };
}

export type ColumnFormulaWrite = { recipe_id: string; amount_usd: number };

/**
 * One column, one rate. Missing size clears that row instead of writing $0.
 * `onlyRecipeIds` prices just those recipes and writes $0 on every other product,
 * option, and package.
 */
export function planColumnFormulaWrites(
  products: HaulProductInput[],
  basis: HaulRateBasis,
  rate_usd: number,
  productsOnly = true,
  onlyRecipeIds?: readonly string[] | null,
): {
  writes: ColumnFormulaWrite[];
  clear_recipe_ids: string[];
  missing_size: HaulEvalPlan["missing_size"];
} {
  const writes: ColumnFormulaWrite[] = [];
  const clear_recipe_ids: string[] = [];
  const missing_size: HaulEvalPlan["missing_size"] = [];
  const rule: HaulRateRuleInput = {
    id: "column",
    scope: "group",
    group_id: null,
    recipe_id: null,
    basis,
    plant_rate_usd: rate_usd,
    lot_rate_usd: rate_usd,
  };
  const only = onlyRecipeIds != null ? new Set(onlyRecipeIds.map((id) => id.toLowerCase())) : null;
  const pricedKinds = productsOnly ? new Set(["product"]) : new Set(["product", "option", "package"]);
  const catalogKinds = new Set(["product", "option", "package"]);
  for (const product of products) {
    if (only) {
      if (!catalogKinds.has(product.kind)) continue;
      if (!only.has(product.recipe_id.toLowerCase())) {
        writes.push({ recipe_id: product.recipe_id, amount_usd: 0 });
        continue;
      }
    } else if (!pricedKinds.has(product.kind)) {
      continue;
    }
    const amounts = haulAmountsForRule(rule, haulSizeForProduct(product));
    if (!amounts) {
      clear_recipe_ids.push(product.recipe_id);
      missing_size.push({
        recipe_id: product.recipe_id,
        recipe_code: product.recipe_code,
        name: product.name,
      });
      continue;
    }
    writes.push({ recipe_id: product.recipe_id, amount_usd: amounts.plant_usd });
  }
  return { writes, clear_recipe_ids, missing_size };
}

export function resolveHaulRule(
  product: HaulProductInput,
  rules: HaulRateRuleInput[],
): HaulRateRuleInput | null {
  if (product.kind !== "product") return null;
  const byRecipe = rules.find((rule) => rule.scope === "recipe" && rule.recipe_id === product.recipe_id);
  if (byRecipe) return byRecipe;
  if (!product.recipe_group_id) return null;
  return rules.find((rule) => rule.scope === "group" && rule.group_id === product.recipe_group_id) ?? null;
}

function roundUsd(amount: number): number {
  return Math.round(amount * 100) / 100;
}

/** Null when the basis needs a size and the product has none. `none` is $0 on both sides. */
export function haulAmountsForRule(
  rule: HaulRateRuleInput,
  size: { width: number; length: number } | null,
): { plant_usd: number; lot_usd: number } | null {
  if (rule.basis === "none") return { plant_usd: 0, lot_usd: 0 };
  let measure = 1;
  if (rule.basis !== "flat_usd") {
    if (!size) return null;
    measure = rule.basis === "building_sq_ft" ? size.width * size.length : size.length;
  }
  return {
    plant_usd: roundUsd(rule.plant_rate_usd * measure),
    lot_usd: roundUsd(rule.lot_rate_usd * measure),
  };
}

/**
 * Product recipes only. A recipe rule wins over the group. No rule, or a size the
 * basis cannot read, leaves the layer values unset (listed in clear_recipe_ids).
 * Options and packages are omitted from both lists.
 */
export function planHaulLayerWrites(
  products: HaulProductInput[],
  rules: HaulRateRuleInput[],
): HaulEvalPlan {
  const writes: HaulDollarWrite[] = [];
  const clear_recipe_ids: string[] = [];
  const missing_size: HaulEvalPlan["missing_size"] = [];
  for (const product of products) {
    if (product.kind !== "product") continue;
    const rule = resolveHaulRule(product, rules);
    if (!rule) {
      clear_recipe_ids.push(product.recipe_id);
      continue;
    }
    const amounts = haulAmountsForRule(rule, haulSizeForProduct(product));
    if (!amounts) {
      clear_recipe_ids.push(product.recipe_id);
      missing_size.push({
        recipe_id: product.recipe_id,
        recipe_code: product.recipe_code,
        name: product.name,
      });
      continue;
    }
    writes.push({ recipe_id: product.recipe_id, ...amounts });
  }
  return { writes, clear_recipe_ids, missing_size };
}
